import type { NoteCreateRequest, NoteTextRequest, NoteView } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент заметок §6.4 запросного канала §7.6. Маршруты — `/api/notes` и
 * `/api/notes/{id}`; исходы различаются статусом (design.md решение 2), как у
 * `density-api.ts`: сессия выражается кодом HTTP, а не сообщением контракта.
 */

const NOTES_BASE = "/api/notes";

function isNoteView(value: unknown): value is NoteView {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.base === "string" &&
    typeof value.text === "string" &&
    typeof value.created_at_ms === "number" &&
    typeof value.updated_at_ms === "number"
  );
}

/**
 * Список своих заметок §6.4 целиком (личные с ТЗ 1.115, §134). «Нет сессии» (`401`) отделена от
 * недоступности демона по той же причине, что и у `fetchCurrentUser`:
 * смешение выбросило бы вошедшего человека на форму входа при обычном сбое
 * хранилища.
 */
export type NotesListResult =
  | { status: "ok"; notes: NoteView[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchNotes(): Promise<NotesListResult> {
  let response: Response;
  try {
    response = await fetch(NOTES_BASE, { signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS) });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список заметок отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isNoteView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", notes: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход записи одной заметки: создание или правка текста. `invalid` — пустой
 * текст после обрезки (§6.4), `not-found` — только у правки.
 */
export type NoteWriteResult =
  | { status: "ok"; note: NoteView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "not-found" }
  | { status: "unavailable" };

export async function createNote(request: NoteCreateRequest): Promise<NoteWriteResult> {
  let response: Response;
  try {
    response = await fetch(NOTES_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  return readNoteWriteResponse(response, "заметка не создана демоном");
}

export async function updateNoteText(
  id: string,
  request: NoteTextRequest,
): Promise<NoteWriteResult> {
  let response: Response;
  try {
    response = await fetch(`${NOTES_BASE}/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  return readNoteWriteResponse(response, "заметка не обновлена демоном");
}

async function readNoteWriteResponse(
  response: Response,
  logMessage: string,
): Promise<NoteWriteResult> {
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (response.status === 404) {
    return { status: "not-found" };
  }
  if (!response.ok) {
    console.error(logMessage, response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isNoteView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", note: body };
  } catch {
    return { status: "unavailable" };
  }
}

export type NoteDeleteResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "not-found" }
  | { status: "unavailable" };

export async function deleteNote(id: string): Promise<NoteDeleteResult> {
  let response: Response;
  try {
    response = await fetch(`${NOTES_BASE}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 404) {
    return { status: "not-found" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  console.error("заметка не удалена демоном", response.status);
  return { status: "unavailable" };
}
